import { useEffect, useRef, useState } from "react";
import { storage } from "../firebase";
import {
  getCachedDownloadUrl,
  removeCachedStorageUrl,
} from "../utils/storageCache";

function useNearViewport(rootMargin = "300px") {
  const ref = useRef(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element || isNear) return;

    if (!("IntersectionObserver" in window)) {
      setIsNear(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isNear, rootMargin]);

  return [ref, isNear];
}

function StorageImage({
  path,
  version = "",
  alt = "Imagen",
  className = "",
  fallback = "📦",
  enabled = true,
}) {
  const cleanPath = String(path || "").trim();

  const [containerRef, isNearViewport] = useNearViewport();
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadUrl() {
      if (!enabled || !cleanPath || !isNearViewport) return;

      try {
        const downloadUrl = await getCachedDownloadUrl(storage, cleanPath, {
          version,
        });

        if (active) {
          setUrl(downloadUrl);
          setFailed(false);
        }
      } catch (error) {
        console.error("No se pudo cargar imagen de Storage:", cleanPath, error);

        if (active) {
          setUrl("");
          setFailed(true);
        }
      }
    }

    loadUrl();

    return () => {
      active = false;
    };
  }, [cleanPath, version, enabled, isNearViewport]);

  if (failed) {
    return (
      <span
        ref={containerRef}
        className={className}
        title={`No se pudo cargar: ${cleanPath || "sin ruta"}`}
      >
        ⚠️
      </span>
    );
  }

  if (!url) {
    return (
      <span
        ref={containerRef}
        className={className}
        title={cleanPath || "Sin imagen"}
      >
        {fallback}
      </span>
    );
  }

  return (
    <img
      ref={containerRef}
      className={className}
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      title={cleanPath}
      onError={() => {
        removeCachedStorageUrl(cleanPath);
        setUrl("");
        setFailed(true);
      }}
    />
  );
}

export default StorageImage;